/**
 * Chart 4 while tuning: instant threat against passive income, **at the
 * margin** — the quantity the balance pass exists to shape.
 *
 * It replaces the biter-strength chart rather than joining it, because the four
 * rows share one cursor and one zoom and a fifth would push the set past what
 * fits above the fold. Strength is a consequence of the curves; this is the
 * curve being chosen.
 */

import { useCallback, useState } from 'react';

import { formatCompact, type Sample } from '../../model';
import { ChartFrame, type ChartLine } from '../ChartFrame';
import { ScenarioReset } from '../ScenarioPanel';
import { ScaleToggle } from '../controls';

const LINES: ChartLine[] = [
  {
    key: 'ratio',
    label: 'Minutes',
    color: '#7de3c3',
    accessor: (s: Sample) => s.ratioMinutes,
  },
];

function RatioInfo() {
  return (
    <>
      <p className="formula">
        d(instant)/dM ÷ 60 · d(passive)/dM — both per unit of mutagen, so the mutagen cancels
      </p>
      <p>
        How many <b>minutes of passive income</b> the instant threat of the next flask is worth. Ten
        means a send lands like ten minutes of the income that same send also buys, so timing a
        push beats sitting on the stock; below one, feeding is an investment and nothing else.
      </p>
      <p>
        <b>Marginal, not cumulative.</b> The cumulative version — everything fed so far over the
        income it bought — is dominated by the nearly-free first slice of evolution and says almost
        nothing about whether the <i>next</i> send is worth making.
      </p>
      <p>
        Which is why it starts at zero. As evolution approaches nothing, <code>dE/dM</code> diverges,
        so a unit of mutagen buys unbounded income and only a fixed amount of instant threat.
      </p>
      <p>
        With the tangent switch on, the peak sits at <b>exactly 100%</b> for any passive curve that
        bends upward — below it the ratio climbs as <code>E^(1 - 1/p)</code> falls away, above it
        <code>dE/dM</code> is frozen so the ratio can only follow <code>1 / P'(E)</code> down. The
        turn is pinned by the kink in the evolution curve, not by any coefficient, so moving the
        passive constants slides the whole curve up and down and leaves the peak where it is.
      </p>
      <p>
        A branch with no instant threat draws flat along zero — that flat line <i>is</i> the
        comparison.
      </p>
    </>
  );
}

/** Round a positive value out to a whole power of ten. */
const decade = (value: number, round: (n: number) => number) =>
  10 ** round(Math.log10(Math.max(value, 1e-9)));

export function RatioChart() {
  // Linear, like every other row. Live's ratio climbs without bound — 99
  // minutes at 100% evo against the scenario's 26, and 780 by 330% — so while
  // comparing against live the curve being *tuned* is pressed towards the
  // bottom; the toggle is right there and always offered on this row for that
  // reason. Tuning against the scenario alone, which is the usual case, it runs
  // 0 to 26 and linear is the kinder axis anyway.
  const [scaleType, setScaleType] = useState<'linear' | 'log'>('linear');

  // Not monotonic and not endpoint-bounded — the peak is somewhere in the
  // middle by design, so the extent has to be scanned rather than read off the
  // ends the way every other chart here can.
  const yDomain = useCallback(
    (samples: Sample[]): [number, number] => {
      if (!samples.length) return [0, 1];
      let lo = Infinity;
      let hi = -Infinity;
      for (const sample of samples) {
        const value = sample.ratioMinutes;
        if (!Number.isFinite(value)) continue;
        if (scaleType === 'log' && value <= 0) continue;
        if (value < lo) lo = value;
        if (value > hi) hi = value;
      }
      // A branch with no instant threat is all zeros, so in log mode it has
      // nothing to contribute to the extent. It has to say so with a non-finite
      // pair — ChartFrame folds each branch's extent into a shared one and skips
      // non-finite entries, so returning a real [0, 1] here would drag the
      // *shared* floor down to zero and cost the axis five decades.
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
        return scaleType === 'log' ? [NaN, NaN] : [0, 1];
      }
      if (scaleType !== 'log') return [Math.min(lo, 0), hi];
      // The curve leaves zero asymptotically, so the smallest *positive* sample
      // is an artefact of where the sampler happened to land — anchoring the
      // axis to it drags the scale five decades down and crushes the band worth
      // reading into the top fifth of the plot. Three decades below the peak is
      // the whole of the interesting range and stays put as the zoom moves.
      // Snapped out to whole decades so the axis is labelled 0.1 / 1 / 10 / 100
      // at every zoom rather than by wherever the data happened to stop.
      const floor = Math.max(hi / 1e3, 1e-4);
      return [decade(Math.max(lo, floor), Math.floor), decade(hi, Math.ceil)];
    },
    [scaleType],
  );

  return (
    <ChartFrame
      index={4}
      title="Instant ÷ passive"
      info={<RatioInfo />}
      lines={LINES}
      yDomain={yDomain}
      yScaleType={scaleType}
      // The domain is decade-snapped above, so 2 is enough for d3 to hand back
      // whole decades at any zoom instead of subdividing them.
      yNumTicks={scaleType === 'log' ? 2 : 4}
      formatY={(value) => formatCompact(value, 1)}
      yLabel="minutes"
      showBottomAxis
      controls={<ScaleToggle value={scaleType} onChange={setScaleType} />}
      // This row is only ever drawn while tuning and is always the last one, so
      // its rail foot is the bottom right corner of the chart panel.
      railFoot={<ScenarioReset />}
    />
  );
}
